import {
  ITabletSubmission,
  ITabletSubmissionStatus,
} from '../Interfaces/ITabletSubmission';

/**
 * Beschriftungen der Ident-Service-Erfassungsliste.
 *
 * Getrennt von der Gruppierung und ohne React-Abhängigkeit, damit die reine
 * Ableitung testbar bleibt (siehe tabletSubmissionLabels.test.ts).
 */

/**
 * Ab wann ein Ausweis als „läuft bald ab" gilt — Notariatssicht.
 *
 * 90 Tage, einheitlich für Erfassungsliste und Prüf-Dialog; die Tablet-Maske
 * nutzt denselben Wert (`checkExpiry` in TabletApp/src/utils/mrzParser.ts).
 * Das Mandantenportal warnt bewusst früher (6 Monate): dort geht es darum,
 * dem Mandanten Zeit für eine Verlängerung zu geben, nicht um die Gültigkeit
 * am Beurkundungstermin.
 */
export const EXPIRY_WARN_DAYS = 90;

export type SubmissionSource = 'tablet' | 'portal';

const STATUS_LABEL_TABLET: Record<ITabletSubmissionStatus, string> = {
  pending_input: 'Wartet auf Eingabe am Tablet',
  submitted: 'Eingang vom Tablet',
  accepted: 'Übernommen',
  rejected: 'Abgelehnt',
  cancelled: 'Abgebrochen',
};

const STATUS_LABEL_PORTAL: Record<ITabletSubmissionStatus, string> = {
  pending_input: 'Aufforderung versendet',
  submitted: 'Eingang aus dem Mandantenportal',
  accepted: 'Übernommen',
  rejected: 'Abgelehnt',
  cancelled: 'Abgebrochen',
};

export const STATUS_COLOR: Record<
  ITabletSubmissionStatus,
  'default' | 'primary' | 'success' | 'warning' | 'error' | 'info'
> = {
  pending_input: 'info',
  submitted: 'primary',
  accepted: 'success',
  rejected: 'error',
  cancelled: 'default',
};

/**
 * Status-Beschriftung abhängig von der Quelle.
 *
 * Vorher war das ein reines `Record<Status, string>`: eine Erfassung, die der
 * Mandant im Portal eingereicht hat, wurde als „Eingang vom Tablet" angezeigt.
 * Bei unbekannter Quelle bleibt es beim Tablet-Wortlaut — das war das
 * Verhalten vor Einführung des Portal-Kanals.
 */
export function statusLabel(
  status: ITabletSubmissionStatus,
  source?: string | null
): string {
  return source === 'portal'
    ? STATUS_LABEL_PORTAL[status]
    : STATUS_LABEL_TABLET[status];
}

/**
 * Woher die Erfassung kommt — für die Spalte „Quelle" und den Dialog-
 * Untertitel. Bei Portal-Erfassungen gibt es kein Gerät; vorher stand dort
 * `–` bzw. wörtlich `Tablet „–"`.
 */
export function sourceLabel(
  submission: ITabletSubmission | undefined | null
): string {
  if (!submission) return '–';
  if (submission.Source === 'portal') return 'Mandantenportal';
  return submission.TabletName || 'Tablet';
}

function parsePayload(value: unknown): Record<string, unknown> | null {
  if (!value) return null;
  if (typeof value === 'object') return value as Record<string, unknown>;
  try {
    return JSON.parse(String(value)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * Ablaufdatum des Ausweises.
 *
 * `Person.DocumentExpiry` steht im **eingereichten** `Payload`. Vorher wurde
 * `PrefillPayload` gelesen — und `_buildPrefillFromLegalClient` schreibt das
 * Feld nie, weil DiNo es am LegalClient nicht führt. Die Ablauf-Chips konnten
 * dadurch nie erscheinen. `PrefillPayload` bleibt als Fallback, falls ein
 * Aufrufer die Vorbefüllung explizit mit einem Ablaufdatum mitgibt.
 */
export function documentExpiryOf(
  submission: ITabletSubmission | undefined | null
): string | null {
  if (!submission) return null;
  for (const raw of [submission.Payload, submission.PrefillPayload]) {
    const parsed = parsePayload(raw);
    const person = parsed?.['Person'] as Record<string, unknown> | undefined;
    const expiry = person?.['DocumentExpiry'];
    if (typeof expiry === 'string' && expiry.trim() !== '') return expiry;
  }
  return null;
}

export type ExpiryState = 'valid' | 'expiring_soon' | 'expired';

export interface ExpiryInfo {
  state: ExpiryState;
  days: number;
  formattedDate: string;
}

/** Ablauf-Auswertung, gemeinsam für Liste und Prüf-Dialog. */
export function expiryInfoOf(
  expiry: string | null | undefined,
  warnDays: number = EXPIRY_WARN_DAYS,
  today: Date = new Date()
): ExpiryInfo | null {
  if (!expiry) return null;
  const exp = new Date(expiry);
  if (Number.isNaN(exp.getTime())) return null;
  const from = new Date(today.getTime());
  from.setHours(0, 0, 0, 0);
  exp.setHours(0, 0, 0, 0);
  const days = Math.floor(
    (exp.getTime() - from.getTime()) / (1000 * 60 * 60 * 24)
  );
  const formattedDate = exp.toLocaleDateString('de-DE');
  if (days < 0) return { state: 'expired', days, formattedDate };
  if (days <= warnDays) return { state: 'expiring_soon', days, formattedDate };
  return { state: 'valid', days, formattedDate };
}

export interface PhotosRemovedInfo {
  reason: string;
  at?: string;
  sides: string[];
}

/**
 * Vermerk, wenn die Ausweisfotos planmäßig entfernt wurden (Ticket #1273).
 *
 * Die Fotos liegen nach der Übernahme als Dokument (Typ „Personalausweis") am
 * Beteiligten; im `Payload` wären sie eine zweite, unbefristete Kopie und
 * werden deshalb entfernt — bei Ablehnung und Abbruch ebenfalls. Ohne diesen
 * Hinweis verschwindet der Fotoblock im Prüf-Dialog stillschweigend, und ein
 * Mitarbeiter kann nicht unterscheiden, ob nie Fotos dabei waren oder ob sie
 * planmäßig gelöscht wurden.
 */
export function photosRemovedInfo(
  submission: ITabletSubmission | undefined | null
): PhotosRemovedInfo | null {
  if (!submission) return null;
  const parsed = parsePayload(submission.Payload);
  const idScan = parsed?.['IdScan'] as Record<string, unknown> | undefined;
  const marker = idScan?.['PhotosRemoved'];
  if (!marker) return null;
  // Die Migration für den Altbestand schreibt ein Objekt; ein einfacher String
  // wird trotzdem angenommen, damit der Hinweis nicht an der Form scheitert.
  if (typeof marker === 'string') return { reason: marker, sides: [] };
  if (typeof marker !== 'object') return null;
  const m = marker as Record<string, unknown>;
  const sides = Array.isArray(m['Sides'])
    ? (m['Sides'] as unknown[]).filter(
        (x): x is string => typeof x === 'string'
      )
    : [];
  return {
    reason: typeof m['Reason'] === 'string' ? m['Reason'] : 'entfernt',
    at: typeof m['At'] === 'string' ? m['At'] : undefined,
    sides,
  };
}

/**
 * Satz für den Prüf-Dialog: wo die Ausweisfotos geblieben sind.
 *
 * Bewusst kurz. Wichtig ist für den Mitarbeiter genau eine Auskunft — dass die
 * Fotos beim Beteiligten liegen und unter welcher Dokumentart. Dass sie in der
 * Erfassung nicht doppelt aufbewahrt werden, ist unsere Begründung und keine
 * Information, die er zum Arbeiten braucht; wo er sie sieht, zeigt der Block
 * direkt darunter.
 *
 * `documentTypeLabel` kommt vom Aufrufer, weil seit #1299 auch ein Reisepass
 * abgelegt werden kann. Ein fest verdrahtetes „Personalausweis" wäre für die
 * genau falsch.
 */
export function photosRemovedText(
  info: PhotosRemovedInfo,
  documentTypeLabel = 'Personalausweis'
): string {
  const seiten = info.sides.length ? ` (${info.sides.join(', ')})` : '';
  if (/^Uebernommen|^Übernommen/i.test(info.reason)) {
    return (
      `Ausweisfotos${seiten} als „${documentTypeLabel}" am Beteiligten ` +
      `abgelegt.`
    );
  }
  if (/^Abgelehnt/i.test(info.reason)) {
    return `Die Ausweisfotos${seiten} wurden mit der Ablehnung gelöscht.`;
  }
  if (/^Abgebrochen/i.test(info.reason)) {
    return `Die Ausweisfotos${seiten} wurden mit dem Abbruch gelöscht.`;
  }
  return `Die Ausweisfotos${seiten} wurden entfernt: ${info.reason}.`;
}

/** Trägt die Erfassung noch Ausweisfotos? */
export function submissionHasIdPhotos(
  submission: ITabletSubmission | undefined | null
): boolean {
  if (!submission) return false;
  const parsed = parsePayload(submission.Payload);
  const idScan = parsed?.['IdScan'] as Record<string, unknown> | undefined;
  if (!idScan) return false;
  return Object.keys(idScan).some(
    key => key.endsWith('Base64') && Boolean(idScan[key])
  );
}

/**
 * Text der Ablehnungs-Rückfrage.
 *
 * Muss die Folge benennen: seit #1273 werden die Ausweisfotos mit der Ablehnung
 * **gelöscht** und nicht am Beteiligten hinterlegt — anders als bei der
 * Übernahme. Der frühere Text („Die Daten bleiben zur Ansicht erhalten") war
 * danach falsch, und zwar genau in dem Dialog, der die Entscheidung trägt.
 *
 * Was danach möglich ist, hängt an der Quelle: eine abgelehnte Portal-Erfassung
 * öffnet die Aufforderung automatisch wieder (#1271), beim Tablet-Weg braucht es
 * einen neuen Auftrag.
 */
export function rejectConfirmText(
  submission: ITabletSubmission | undefined | null
): string {
  const parts: string[] = [];
  if (submissionHasIdPhotos(submission)) {
    parts.push(
      'Die eingereichten Ausweisfotos werden dabei gelöscht. Sie werden ' +
        'nicht als Dokument am Beteiligten hinterlegt — das passiert nur bei ' +
        'der Übernahme.'
    );
    parts.push(
      'Name, Geburtsdatum und Adresse der Erfassung bleiben zur Ansicht ' +
        'erhalten.'
    );
  } else {
    parts.push('Die Erfassung wird nicht in den Vorgang übernommen.');
  }
  parts.push(
    submission?.Source === 'portal'
      ? 'Der Beteiligte wird anschließend erneut zur Erfassung aufgefordert ' +
          'und kann neue Fotos hochladen.'
      : 'Für eine neue Erfassung muss ein neuer Tablet-Auftrag erstellt werden.'
  );
  return parts.join(' ');
}
